import PushPinTwoToneIcon from '@mui/icons-material/PushPinTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Divider,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

function ClearPosition({
  customPositions,
  setCustomPositions,
  memberRecords,
  onHistoryCheckpoint = null,
  onAutomaticLayoutWrite = null
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () =>
      document.removeEventListener('mousedown', handleMouseDown, true);
  }, [open]);

  useEffect(() => {
    if (!customPositions?.size) {
      setOpen(false);
      return;
    }
    const invalidKeys = Array.from(customPositions.keys()).filter(
      (k) => k == null || k === 'undefined'
    );
    if (invalidKeys.length > 0) {
      // AUTOMATIC hygiene writer — never an undo step, and never an edit: this
      // sweep runs on mount for any family carrying dead-uid pins, and reporting
      // it as an edit took the lock just for opening (TGCLOUD-1665).
      if (onAutomaticLayoutWrite) onAutomaticLayoutWrite('positions');
      const next = new Map(customPositions);
      invalidKeys.forEach((k) => next.delete(k));
      setCustomPositions(next);
    }
  }, [customPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  const positionedNodes = useMemo(() => {
    if (!customPositions?.size) return [];
    return Array.from(customPositions.keys())
      .filter((uid) => uid != null && uid !== 'undefined')
      .map((uid) => {
        const member = memberRecords?.find(
          (m) => String(m._id) === String(uid) || String(m.uid) === String(uid)
        );
        const name = [member?.firstName, member?.lastName]
          .filter(Boolean)
          .join(' ');
        const upn = member?.upn || member?.pid;
        // A pin whose key resolves to no current member (historical dead-uid
        // damage, or a person since removed) is kept — clearing it is the
        // user's call — but labelled instead of rendering "UPN undefined".
        return { uid, name, upn, unresolved: !member };
      });
  }, [customPositions, memberRecords]);

  // UNDO checkpoints on the user-intent clears only — the invalid-key hygiene
  // effect above is an automatic writer and must never create an undo step.
  const clearOne = (uid) => {
    if (onHistoryCheckpoint) onHistoryCheckpoint();
    const next = new Map(customPositions);
    next.delete(uid);
    setCustomPositions(next);
  };

  const clearAll = () => {
    if (onHistoryCheckpoint) onHistoryCheckpoint();
    setCustomPositions(new Map());
  };

  const hasPositions = Boolean(customPositions?.size);

  return (
    <Box
      ref={containerRef}
      sx={{ display: 'inline-flex', alignItems: 'center' }}
    >
      <Tooltip
        title={hasPositions ? 'Custom positions' : 'No custom positions'}
      >
        <span>
          <IconButton
            ref={anchorRef}
            data-va-anchor="pedigree.toolbar.pins"
            size="small"
            onClick={() => setOpen((prev) => !prev)}
            disabled={!hasPositions}
          >
            <PushPinTwoToneIcon
              sx={{
                fontSize: 22,
                color: hasPositions ? '#3d4551' : undefined
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal
        sx={{ zIndex: 1200 }}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
            <Paper elevation={3} sx={{ width: 260, mt: 0.5 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600
                  }}
                >
                  Custom Positions
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={clearAll}
                  sx={{ minWidth: 0, px: 1, fontSize: 12 }}
                >
                  Clear All
                </Button>
              </Box>
              <Divider />
              <List
                dense
                disablePadding
                sx={{ maxHeight: 300, overflowY: 'auto' }}
              >
                {positionedNodes.map(({ uid, name, upn, unresolved }) => (
                  <ListItem
                    key={uid}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => clearOne(uid)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        unresolved
                          ? 'Unmatched pin (person not on this pedigree)'
                          : `UPN ${upn} · ${name}`
                      }
                      slotProps={{
                        primary: {
                          fontSize: 13,
                          noWrap: true,
                          ...(unresolved ? { fontStyle: 'italic' } : {})
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

export default ClearPosition;
